import type { DrawOptions, RoundRobinStandingsSettings } from '@/draw/types';

/** The two option keys a draw's round-robin stages can be ranked under. */
export type RoundRobinSettingsSource = Pick<DrawOptions, 'roundRobinSettings' | 'qualifyingPools'>;

/**
 * The scoring system and tiebreakers a round-robin stage is ranked by.
 *
 * A qualifying pool (`context === 'qualifying'`) plays under the pools' own settings — the
 * Qualifying pools section's "Pool scoring" block, `qualifyingPools.roundRobinAdvancement` —
 * and falls back to the main draw's (`roundRobinSettings`) only when the pools have none, as on
 * Pool Play-offs, whose single block writes the main key. Every other round-robin stage (a
 * round-robin main draw, a ladder) plays under the main draw's settings, falling back to the
 * pools' when the main draw has none.
 *
 * Until 2026-09 the main draw's settings won for every stage, pools included, so "Pool scoring"
 * on the Round Robin template changed nothing. Both runtime readers resolve through here.
 */
export function resolveRoundRobinSettings(
  options: RoundRobinSettingsSource | null | undefined,
  stage: { context?: string | null },
): RoundRobinStandingsSettings | undefined {
  const main = options?.roundRobinSettings;
  const pool = options?.qualifyingPools?.roundRobinAdvancement;
  return stage.context === 'qualifying' ? (pool ?? main) : (main ?? pool);
}
